import { useEffect, useRef, useState } from 'react'
import { useHideFloatingToolbar } from './useBrowserSettings'

export const useHideOnScroll = (pixelsBeforeUp = 3, container?: HTMLElement | null) => {
  const [staticFloatingToolbar] = useHideFloatingToolbar()
  const [hiddenFromScroll, setHiddenFromScroll] = useState(false)

  const stoppedPosition = useRef<number>(0)
  const scrollPosition = useRef<number>(0)

  const scrollListener = useRef<(e: Event) => void>(() => null)
  const scrollSetterTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (staticFloatingToolbar) return

    const scrollElement = container || window

    scrollElement.removeEventListener('scroll', scrollListener.current)

    scrollListener.current = (_e: Event) => {
      const scrollFromTop = (container || document.documentElement).scrollTop
      const scrollingDown = scrollPosition.current < scrollFromTop + pixelsBeforeUp

      scrollPosition.current = scrollFromTop >= 0 ? scrollFromTop : 0 // used To tell the scroll direction

      if (scrollSetterTimeout.current) clearTimeout(scrollSetterTimeout.current)

      scrollSetterTimeout.current = setTimeout(() => {
        stoppedPosition.current = scrollFromTop >= 0 ? scrollFromTop : 0
      }, 10)

      if (scrollFromTop === 0) return setHiddenFromScroll(false)

      // These are the states we already want, if these are set leave them alone
      if (scrollingDown && hiddenFromScroll) return
      if (!scrollingDown && !hiddenFromScroll) return

      if (scrollingDown && scrollPosition.current - pixelsBeforeUp > stoppedPosition.current) setHiddenFromScroll(true)
      if (!scrollingDown && scrollPosition.current + pixelsBeforeUp < stoppedPosition.current)
        setHiddenFromScroll(false)
    }

    scrollElement.addEventListener('scroll', scrollListener.current)

    return () => scrollElement.removeEventListener('scroll', scrollListener.current)
  }, [container, hiddenFromScroll, staticFloatingToolbar])

  if (staticFloatingToolbar) return false

  return hiddenFromScroll
}

export const useStopBodyScroll = (open: boolean) => {
  const thisComponentEnabledNoScroll = useRef(false)

  const [selfEnabledNoScroll, setSelfEnabledNoScroll] = useState(open)

  useEffect(() => {
    if (!open) return

    const scrollingIsActive = !document.body.classList.contains('no-scrolling')

    if (selfEnabledNoScroll) {
      if (scrollingIsActive) {
        thisComponentEnabledNoScroll.current = true
        document.body.classList.add('no-scrolling')
      }
    } else {
      if (thisComponentEnabledNoScroll.current) {
        thisComponentEnabledNoScroll.current = false
        document.body.classList.remove('no-scrolling')
      }
    }

    return () => {
      if (thisComponentEnabledNoScroll.current) {
        document.body.classList.remove('no-scrolling')
      }
    }
  }, [open, selfEnabledNoScroll])

  return setSelfEnabledNoScroll
}

export const useApplyBodyClass = (open: boolean, className: string) => {
  useEffect(() => {
    if (open) document.body.classList.add(className)
    else document.body.classList.remove(className)

    return () => {
      document.body.classList.remove(className)
    }
  }, [open])
}

export const useEnableClassNameOnScrollDown = (className: string, scrollingDown: boolean) => {
  const scrollbarOpenOnMount = useRef<boolean | null>(null)

  useEffect(() => {
    scrollbarOpenOnMount.current = document.body.classList.contains(className)

    return () => {
      if (scrollbarOpenOnMount.current) document.body.classList.add(className)
      else document.body.classList.remove(className)
    }
  }, [])

  useEffect(() => {
    if (scrollingDown) document.body.classList.remove(className)
    else document.body.classList.add(className)
  }, [scrollingDown])
}

export const useIsNearTop = (scrollElement?: HTMLElement | null) => {
  const [isNearTop, setIsNearTop] = useState(false)

  const scrollListener = useRef<(e: Event) => void>(() => null)
  const scrollSetterTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (!scrollElement) return setIsNearTop(false)

    setIsNearTop(scrollElement.scrollTop < 10)

    scrollElement.removeEventListener('scroll', scrollListener.current)

    scrollListener.current = (_e: Event) => {
      if (scrollSetterTimeout.current) clearTimeout(scrollSetterTimeout.current)

      scrollSetterTimeout.current = setTimeout(() => {
        setIsNearTop(scrollElement.scrollTop < 10)
      }, 10)
    }

    scrollElement.addEventListener('scroll', scrollListener.current)

    return () => scrollElement.removeEventListener('scroll', scrollListener.current)
  }, [scrollElement])

  return isNearTop
}
